/**
 * A aba "Conversas" do card da oportunidade (24/09/2026).
 *
 * Todas as conversas do cliente, a mais recente primeiro, as desta
 * oportunidade marcadas. Cada uma com a história: quando abriu, quando fechou e
 * quem fechou, quando reabriu. O contrato explica por que conversas antigas
 * mostram menos.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { EventoDaConversa } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Cartao, Chip } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';

const SITUACAO: Readonly<Record<string, string>> = {
  aberta: 'aberta',
  pendente: 'pendente',
  resolvida: 'resolvida',
  em_espera: 'adiada',
};

const CANAL: Readonly<Record<string, string>> = {
  whatsapp_cloud: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
  telegram: 'Telegram',
  web_widget: 'Chat do site',
  api: 'Integração',
};

function quando(instante: string): string {
  return new Date(instante).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function frase(evento: EventoDaConversa): string {
  if (evento.tipo === 'aberta') return `Aberta em ${quando(evento.quando)}`;
  const verbo = evento.tipo === 'resolvida' ? 'Resolvida' : 'Reaberta';
  return `${verbo} em ${quando(evento.quando)}${evento.por ? ` por ${evento.por}` : ''}`;
}

export function ConversasDaOportunidade({
  oportunidadeId,
  aoAbrirConversa,
}: {
  oportunidadeId: string;
  aoAbrirConversa: (conversaId: string) => void;
}): ReactNode {
  const api = useApi();
  const consulta = useQuery({
    queryKey: chaves.conversasDaOportunidade(oportunidadeId),
    enabled: api !== null,
    queryFn: ({ signal }) =>
      api!.conversasDaOportunidade({ caminho: { id: oportunidadeId }, sinal: signal }),
  });

  if (consulta.isPending) return <p>Buscando as conversas…</p>;
  if (consulta.isError) {
    return (
      <Faixa tom="bloqueado">
        {recadoDeErro(consulta.error, { generica: 'Não foi possível buscar as conversas agora.' })}
      </Faixa>
    );
  }
  const { cliente, itens, tem_mais: temMais } = consulta.data;
  if (!cliente) {
    return (
      <p className="conversas-op__vazio">
        Esta oportunidade ainda não tem cliente reconhecido, então não há conversas para mostrar.
      </p>
    );
  }
  if (itens.length === 0) {
    return (
      <p className="conversas-op__vazio">
        Nenhuma conversa com {cliente.nome ?? 'este cliente'} ainda.
      </p>
    );
  }

  return (
    <div className="conversas-op">
      <ul
        className="conversas-op__lista"
        aria-label={`Conversas com ${cliente.nome ?? 'o cliente'}`}
      >
        {itens.map((conversa) => (
          <Cartao key={conversa.id} como="li" variante="folgado" parte="conversas-op__item">
            <div className="conversas-op__cabecalho">
              <strong>{conversa.caixa ?? CANAL[conversa.canal ?? ''] ?? 'Conversa'}</strong>
              <Chip>{SITUACAO[conversa.situacao] ?? conversa.situacao}</Chip>
              {conversa.desta_oportunidade && <Chip tom="etapa">desta oportunidade</Chip>}
            </div>
            <ol className="conversas-op__eventos" aria-label="O que aconteceu nesta conversa">
              {conversa.eventos.map((evento, i) => (
                <li key={`${evento.tipo}-${evento.quando}-${i}`}>
                  <time dateTime={evento.quando}>{frase(evento)}</time>
                </li>
              ))}
            </ol>
            <span className="admin__form-acoes">
              <Botao miudo onClick={() => aoAbrirConversa(conversa.id)}>
                Abrir conversa
              </Botao>
            </span>
          </Cartao>
        ))}
      </ul>
      {temMais && (
        <p className="conversas-op__vazio">
          Há conversas mais antigas. O Cliente 360 mostra todas.
        </p>
      )}
    </div>
  );
}
